import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getMenuApi,
  addMenuApi,
  toggleMenuApi,
  uploadMenuExcelApi,
  updateMenuApi,
  deleteMenuApi,
} from "../../api/menuApi";

// Fetch all menu items
export const fetchMenu = createAsyncThunk(
  "menu/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getMenuApi();
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Add new menu item
export const addMenuItem = createAsyncThunk(
  "menu/add",
  async (data, { rejectWithValue }) => {
    try {
      const res = await addMenuApi(data);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Toggle menu item availability
export const toggleMenuItem = createAsyncThunk(
  "menu/toggle",
  async (id, { rejectWithValue }) => {
    try {
      const res = await toggleMenuApi(id);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Upload Excel file
export const uploadMenuExcel = createAsyncThunk(
  "menu/uploadExcel",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await uploadMenuExcelApi(formData);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update menu item
export const updateMenuItem = createAsyncThunk(
  "menu/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      // id ko FormData me add karo
      data.append("id", id);

      const res = await updateMenuApi(data);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


// Delete menu item
export const deleteMenuItem = createAsyncThunk(
  "menu/delete",
  async (id, { rejectWithValue }) => {
    try {
      const res = await deleteMenuApi(id);
      return { id, ...res.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  list: [],
  loading: false,
  error: null,
  success: false,
  message: "",
  uploadProgress: 0,
};

// Create slice
const menuSlice = createSlice({
  name: "menu",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.message = "";
    },
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    resetMenuState: (state) => {
      state.list = [];
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = "";
      state.uploadProgress = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Menu
      .addCase(fetchMenu.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMenu.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchMenu.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Add Menu Item
      .addCase(addMenuItem.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addMenuItem.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Menu item added successfully";
        state.list.unshift(action.payload);
      })
      .addCase(addMenuItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      
      // Toggle Menu Item
      .addCase(toggleMenuItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleMenuItem.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Item availability updated";
        const index = state.list.findIndex(
          (i) => i._id === action.payload._id
        );
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      .addCase(toggleMenuItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Upload Excel
      .addCase(uploadMenuExcel.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.uploadProgress = 0;
      })
      .addCase(uploadMenuExcel.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message || "Excel file uploaded successfully";
        state.uploadProgress = 100;
        // Refresh the menu list
        // We'll refetch menu items to get updated list
      })
      .addCase(uploadMenuExcel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
        state.uploadProgress = 0;
      })
      
      // Update Menu Item
      .addCase(updateMenuItem.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateMenuItem.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Menu item updated successfully";
        const index = state.list.findIndex(
          (i) => i._id === action.payload._id
        );
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      .addCase(updateMenuItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      
      // Delete Menu Item
      .addCase(deleteMenuItem.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteMenuItem.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "Menu item deleted successfully";
        state.list = state.list.filter(
          (i) => i._id !== action.payload.id
        );
      })
      .addCase(deleteMenuItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

// Export actions and reducer
export const { 
  clearError, 
  clearSuccess, 
  setUploadProgress,
  resetMenuState 
} = menuSlice.actions;

// Selectors
export const selectMenuList = (state) => state.menu.list;
export const selectMenuLoading = (state) => state.menu.loading;
export const selectMenuError = (state) => state.menu.error;
export const selectMenuSuccess = (state) => state.menu.success;
export const selectMenuMessage = (state) => state.menu.message;
export const selectUploadProgress = (state) => state.menu.uploadProgress;

export default menuSlice.reducer;